import React, { useContext, useState, useEffect } from "react"
import _ from "lodash"
import { Link } from "react-router-dom"
import { useLazyQuery, useMutation } from "@apollo/client"

import { InputSwitch } from "primereact/inputswitch"

import { LoaderMedium } from "../../components/Loaders"
import { ElementLoading } from "../../components/ElementLoading"
import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { useFilters } from "../../components/Filters/useFilters"
import PageHeader from "../../components/PageHeader"
import OrganizationMenuSidebar from "../../components/sidebars/OrganizationMenuSidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"

import { UserContext } from "../../context/userContext"
import { CHANGE_STATUS_LOCATION, GET_LOCATIONS_BY_CTX } from "./queries"

export default function LocationsListOrg() {
  const userCtx = useContext(UserContext)
  const [locations, setLocations] = useState([])
  const [loadingPage, setLoadingPage] = useState(true)
  const [changeStatusLocation] = useMutation(CHANGE_STATUS_LOCATION)

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  const [getLocations] = useLazyQuery(GET_LOCATIONS_BY_CTX, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      organization_id: userCtx.session.user?.organization_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setLocations(
        _.map(data.locations, (location) => ({
          ...location,
          location_id_enterprise_managed:
            location.location_id_enterprise_managed ? "Yes" : "",
          status: location.location_id_enterprise_managed ? (
            `${_.upperFirst(_.toLower(String(location.location_status)))}`
          ) : (
            <ElementLoading
              status={location.location_status}
              item={location.location_id}
              parentCall={(value) =>
                handleToggleChange(value, location.location_id)
              }
              organization_id={location.organization_id}
            >
              <InputSwitch checked={location.location_status === "ACTIVE"} />
            </ElementLoading>
          ),
          edit: location.location_id_enterprise_managed ? null : (
            <Link
              to={`/organization/form/location/1/${location.location_id}`}
              className="btn"
            >
              Edit <i className="pi pi-pencil"></i>
            </Link>
          ),
        }))
      )
      setLoadingPage(false)
    },
  })

  const handleToggleChange = (value, location_id) => {
    return changeStatusLocation({
      variables: {
        staff_id: userCtx?.session.user.staff_id,
        staff_alias: userCtx?.session.user.staff_alias,
        location_id,
        location_status: value === true ? "ACTIVE" : "INACTIVE",
      },
      skip: !userCtx.session.user.staff_id,
      fetchPolicy: "no-cache",
      onCompleted: async () => {
        getLocations()
      },
    })
  }

  useEffect(() => {
    getLocations()
  }, [])

  const filterConfig = [
    {
      label: "Status",
      value: filters.location_status?.value || "",
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "ACTIVE" },
        { label: "Inactive", value: "INACTIVE" },
      ],
      onChange: (e) => onFilterChange("location_status", e.value),
      onClear: () => onFilterChange("location_status", null),
      placeholder: "Select Status",
    },
  ]

  const columns = [
    { field: "location_name", header: "Name", sortable: true },
    {
      field: "location_id_enterprise_managed",
      header: "Enterprise Managed",
      sortable: true,
    },
    { field: "status", header: "Status", sortable: true },
    { field: "edit", header: "Edit", sortable: true },
  ]
  const sidebarContent = <OrganizationMenuSidebar />

  const setToLoading = loadingPage || userCtx.globalLoading

  if (setToLoading)
    return (
      <>
        <PageSidebarContent>{sidebarContent}</PageSidebarContent>
        <LoaderMedium />
      </>
    )

  return (
    <>
      <PageSidebarContent>{sidebarContent}</PageSidebarContent>
      <div className="main-content">
        <PageHeader
          title="Manage Locations"
          actions={[
            {
              to: "/organization/form/location/1/",
              label: "Create new Location",
            },
          ]}
        />
        <GenericTable
          data={locations}
          columns={columns}
          filters={filters}
          header={
            <FilterHeader
              filters={filterConfig}
              globalFilterValue={globalFilterValue}
              onGlobalFilterChange={onGlobalFilterChange}
              clearGlobalFilter={clearGlobalFilter}
              clearAllFilters={clearAllFilters}
            />
          }
          paginator
          rows={50}
          sortField="location_name"
          sortOrder={1}
        />
      </div>
    </>
  )
}
